import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import * as XLSX from "xlsx";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FacilitatorPicker } from "@/components/FacilitatorPicker";
import { toast } from "sonner";
import { ArrowLeft, Plus, UserMinus, Search, Activity, Download, Users2, CheckCircle2, KeyRound } from "lucide-react";

const STATUS_BADGE = {
  not_started: "bg-stone-100 text-stone-600 border-stone-200",
  in_progress: "bg-amber-50 text-amber-800 border-amber-200",
  completed: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

function StatCard({ label, value, suffix }) {
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-5">
      <div className="text-[10px] tracking-[0.18em] uppercase text-stone-500">{label}</div>
      <div className="font-display text-3xl text-stone-900 mt-1.5">
        {value}{suffix && <span className="text-stone-400 text-lg ml-1">{suffix}</span>}
      </div>
    </div>
  );
}

function HeatCell({ value }) {
  // 0-100 → red to green
  const v = Math.max(0, Math.min(100, value || 0));
  let bg = "#f5f5f4";
  let text = "#78716c";
  if (v >= 80) { bg = "#bbf7d0"; text = "#065f46"; }
  else if (v >= 65) { bg = "#fef3c7"; text = "#854d0e"; }
  else if (v >= 45) { bg = "#fed7aa"; text = "#9a3412"; }
  else if (v > 0) { bg = "#fecaca"; text = "#991b1b"; }
  return (
    <div className="text-center py-2 rounded-md text-xs font-medium" style={{ backgroundColor: bg, color: text }}>
      {v > 0 ? v : "—"}
    </div>
  );
}

export default function AdminBatchDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [companyPool, setCompanyPool] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [adding, setAdding] = useState(false);
  const [downloadingCohort, setDownloadingCohort] = useState(false);
  const [limitOpen, setLimitOpen] = useState(false);
  const [limitValue, setLimitValue] = useState("");
  const [savingLimit, setSavingLimit] = useState(false);
  const [facilitators, setFacilitators] = useState([]);
  const [facName, setFacName] = useState("");
  const [facUserId, setFacUserId] = useState(null);
  const [addingAll, setAddingAll] = useState(false);
  const [resettingAll, setResettingAll] = useState(false);
  const [resetSummary, setResetSummary] = useState(null); // array of {name,email,temp_password}
  const [newPOpen, setNewPOpen] = useState(false);
  const [savingNp, setSavingNp] = useState(false);
  const emptyNp = { employee_id: "", name: "", email: "", department: "", designation: "" };
  const [npForm, setNpForm] = useState(emptyNp);

  const downloadCredentialsXlsx = (rows, filename) => {
    const withTemp = (rows || []).filter((p) => p.temp_password && p.temp_password !== "(failed)");
    if (withTemp.length === 0) {
      toast.info("No credentials to download — all participants have already set their own password.");
      return;
    }
    const sheetData = withTemp.map((p) => ({
      "Name": p.name || "",
      "Email": p.email || "",
      "Temporary Password": p.temp_password || "",
    }));
    const ws = XLSX.utils.json_to_sheet(sheetData);
    ws["!cols"] = [{ wch: 24 }, { wch: 32 }, { wch: 20 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Credentials");
    XLSX.writeFile(wb, filename);
    toast.success(`Downloaded ${withTemp.length} credential(s)`);
  };

  useEffect(() => {
    api.get("/admin/facilitators")
      .then((r) => setFacilitators(r.data))
      .catch(() => setFacilitators([]));
  }, []);

  const openLimitEditor = () => {
    setLimitValue(data?.batch?.max_roleplays != null ? String(data.batch.max_roleplays) : "");
    setFacName(data?.batch?.facilitator_name || "");
    setFacUserId(data?.batch?.facilitator_user_id || null);
    setLimitOpen(true);
  };

  const saveLimit = async () => {
    setSavingLimit(true);
    try {
      await api.patch(`/admin/batches/${id}`, {
        max_roleplays: limitValue === "" ? null : parseInt(limitValue, 10),
        facilitator_name: facName,
        facilitator_user_id: facUserId,
      });
      toast.success("Batch settings updated");
      setLimitOpen(false);
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingLimit(false);
    }
  };

  const downloadCohortPdf = async () => {
    setDownloadingCohort(true);
    try {
      const res = await api.get(`/admin/batches/${id}/cohort-report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `leadersim-cohort-${id.slice(-6)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success("Cohort report downloaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDownloadingCohort(false);
    }
  };

  const reload = () => {
    setLoading(true);
    api.get(`/admin/batches/${id}/dashboard`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(reload, [id]);

  const openAdd = async () => {
    setOpen(true);
    setSearch("");
    setSelected(new Set());
    try {
      const r = await api.get(`/admin/companies/${data.batch.company_id}/participants`);
      setCompanyPool(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const enrolledIds = useMemo(
    () => new Set((data?.participants || []).map((p) => p.user_id)),
    [data],
  );

  const filteredPool = useMemo(() => {
    const q = search.toLowerCase();
    return companyPool
      .filter((p) => !enrolledIds.has(p.id))
      .filter((p) =>
        !q ||
        p.name.toLowerCase().includes(q) ||
        p.email.toLowerCase().includes(q) ||
        (p.employee_id || "").toLowerCase().includes(q) ||
        (p.department || "").toLowerCase().includes(q),
      );
  }, [companyPool, search, enrolledIds]);

  const toggle = (uid) => {
    const next = new Set(selected);
    next.has(uid) ? next.delete(uid) : next.add(uid);
    setSelected(next);
  };

  const addSelected = async () => {
    if (selected.size === 0) return;
    setAdding(true);
    try {
      await api.post(`/admin/batches/${id}/participants`, { user_ids: Array.from(selected) });
      toast.success(`${selected.size} participant(s) added`);
      setOpen(false);
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAdding(false);
    }
  };

  const addAllCompanyParticipants = async () => {    setAddingAll(true);
    try {
      const r = await api.get(`/admin/companies/${data.batch.company_id}/participants`);
      const toAdd = r.data.filter((p) => !enrolledIds.has(p.id)).map((p) => p.id);
      if (toAdd.length === 0) {
        toast.info("All company participants are already in this batch");
        return;
      }
      await api.post(`/admin/batches/${id}/participants`, { user_ids: toAdd });
      toast.success(`${toAdd.length} company participant(s) linked to this batch`);
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAddingAll(false);
    }
  };

  const resetAllPasswords = async () => {
    if (!window.confirm(`Reset passwords for all ${participants.length} participant(s) in this batch? Each gets a new unique temporary password.`)) return;
    setResettingAll(true);
    const summary = [];
    let okCount = 0;
    try {
      for (const p of participants) {
        try {
          const r = await api.post(`/admin/participants/${p.user_id}/reset-password`, {});
          summary.push({ name: p.name, email: p.email, temp_password: r.data.temp_password });
          okCount += 1;
        } catch {
          summary.push({ name: p.name, email: p.email, temp_password: "(failed)" });
        }
      }
      setResetSummary(summary);
      const failCount = summary.length - okCount;
      if (failCount === 0) {
        toast.success(`Reset ${okCount} password(s)`);
      } else if (okCount === 0) {
        toast.error(`All ${failCount} resets failed`);
      } else {
        toast.warning(`${okCount} reset, ${failCount} failed`);
      }
      reload();
    } finally {
      setResettingAll(false);
    }
  };

  const copyResetSummary = async () => {
    const text = (resetSummary || []).map((r) => `${r.name} · ${r.email} · ${r.temp_password}`).join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied all credentials to clipboard");
    } catch {
      toast.error("Clipboard blocked by the browser");
    }
  };

  const createParticipantInBatch = async (e) => {
    e.preventDefault();
    setSavingNp(true);
    try {
      const r = await api.post(`/admin/companies/${data.batch.company_id}/participants`, npForm);
      try {
        await api.post(`/admin/batches/${id}/participants`, { user_ids: [r.data.id] });
      } catch (linkErr) {
        toast.error(`Participant created under the company, but linking to this batch failed: ${formatApiError(linkErr)}. Use "Add participants" to link them.`, { duration: 9000 });
        setNewPOpen(false);
        setNpForm(emptyNp);
        reload();
        return;
      }
      toast.success(`Participant created & added · temporary password: ${r.data.temp_password}`, { duration: 9000 });
      setNewPOpen(false);
      setNpForm(emptyNp);
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingNp(false);
    }
  };

  const removeParticipant = async (uid) => {
    if (!window.confirm("Remove this participant from the batch?")) return;
    try {
      await api.delete(`/admin/batches/${id}/participants/${uid}`);
      toast.success("Removed");
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const resetPassword = async (uid, name) => {
    if (!window.confirm(`Reset password for ${name}? A new temporary password will be generated.`)) return;
    try {
      const r = await api.post(`/admin/participants/${uid}/reset-password`, {});
      toast.success(`New temporary password for ${name}: ${r.data.temp_password}`, { duration: 9000 });
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 py-12 text-stone-500 text-sm">Loading batch…</div>
      </div>
    );
  }
  if (!data) return null;
  const { batch, participants, completed_count, completion_pct, average_score, competency_heatmap } = data;

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10">
        <Link to={`/admin/companies/${batch.company_id}`} className="inline-flex items-center text-sm text-stone-500 hover:text-stone-800 mb-5">
          <ArrowLeft className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> {batch.company_name || "Company"}
        </Link>

        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-xs tracking-[0.18em] uppercase text-stone-500 mb-2">{batch.program_name}</div>
            <h1 className="font-display text-3xl md:text-4xl text-stone-900 tracking-tight">{batch.name}</h1>
            <div className="text-sm text-stone-500 mt-1.5 flex items-center gap-3 flex-wrap">
              <span>Facilitator: <b className="text-stone-700">{batch.facilitator_name}</b></span>
              <span>·</span>
              <span>{batch.start_date} → {batch.end_date}</span>
              <span className={`text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full border ${STATUS_BADGE[batch.status] || ""}`}>{batch.status}</span>
            </div>
            <div className="text-sm text-stone-600 mt-2 flex items-center gap-2" data-testid="batch-session-limit">
              <span>Session Limit: <b className="text-stone-800">{batch.max_roleplays != null ? `${batch.max_roleplays} per participant` : "Unlimited"}</b></span>
              <button onClick={openLimitEditor} className="text-[#b81f24] hover:underline text-xs" data-testid="edit-session-limit-btn">Edit</button>
            </div>
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="batch-stats">
          <StatCard label="Total participants" value={participants.length} />
          <StatCard label="Completed" value={completed_count} />
          <StatCard label="Completion" value={completion_pct} suffix="%" />
          <StatCard label="Avg score" value={average_score ?? "—"} suffix={average_score ? "/100" : ""} />
        </div>

        {/* Warning: no participants linked */}
        {participants.length === 0 && (
          <div data-testid="no-participants-warning" className="mb-6 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4">
            <span className="text-xl leading-none">⚠️</span>
            <div className="text-sm text-amber-900">
              <b>No participants are linked to this batch.</b> Analytics and roleplay limits won&apos;t work until participants are added.
              Click <b>&quot;Add All Company Participants&quot;</b> or <b>&quot;Add participants&quot;</b> to link them.
            </div>
          </div>
        )}

        <Tabs defaultValue="participants">
          <TabsList className="bg-stone-100">
            <TabsTrigger value="participants" data-testid="tab-batch-participants">Participants</TabsTrigger>
            <TabsTrigger value="heatmap" data-testid="tab-heatmap">Competency heatmap</TabsTrigger>
          </TabsList>

          {/* PARTICIPANTS */}
          <TabsContent value="participants" className="mt-6">
            <div className="flex justify-end gap-2 mb-4 flex-wrap">
              <Button data-testid="add-all-company-participants-btn" onClick={addAllCompanyParticipants} disabled={addingAll} variant="outline" className="rounded-md h-10 px-5 border-[#b81f24] text-[#b81f24] hover:bg-[#b81f24]/5">
                <Users2 className="w-4 h-4 mr-1.5" strokeWidth={1.75} /> {addingAll ? "Adding…" : "Add All Company Participants"}
              </Button>
              <Button data-testid="new-participant-in-batch-btn" onClick={() => { setNpForm(emptyNp); setNewPOpen(true); }} variant="outline" className="rounded-md h-10 px-5 border-stone-300">
                <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.75} /> New participant
              </Button>
              <Button data-testid="add-batch-participants-btn" onClick={openAdd} className="bg-[#b81f24] hover:bg-[#9a1a1f] text-white rounded-md h-10 px-5">
                <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.75} /> Add participants
              </Button>
              {participants.length > 0 && (
                <Button data-testid="reset-all-passwords-btn" onClick={resetAllPasswords} disabled={resettingAll} variant="outline" className="rounded-md h-10 px-5 border-stone-300">
                  <KeyRound className="w-4 h-4 mr-1.5" strokeWidth={1.75} /> {resettingAll ? "Resetting…" : "Reset all passwords"}
                </Button>
              )}
              {participants.length > 0 && (
                <Button
                  data-testid="download-credentials-btn"
                  variant="outline"
                  onClick={() => {
                    const batchSlug = (batch?.name || "batch").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
                    downloadCredentialsXlsx(participants, `${batchSlug}-credentials.xlsx`);
                  }}
                  className="rounded-md h-10 px-5 border-stone-300"
                >
                  <Download className="w-4 h-4 mr-1.5" strokeWidth={1.75} /> Download Credentials
                </Button>
              )}
            </div>
            {participants.length === 0 ? (
              <div className="bg-white border border-dashed border-stone-200 rounded-3xl p-12 text-center">
                <Users2 className="w-10 h-10 text-stone-300 mx-auto mb-3" strokeWidth={1.25} />
                <div className="font-display text-lg text-stone-900">No participants in this batch yet</div>
                <p className="text-stone-500 text-sm mt-1">Add some from the company pool to get started.</p>
              </div>
            ) : (
              <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid="batch-participants-table">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 text-stone-500 text-[10px] tracking-[0.18em] uppercase">
                    <tr>
                      <th className="text-left px-5 py-3">Name</th>
                      <th className="text-left px-5 py-3">Email</th>
                      <th className="text-left px-5 py-3">Status</th>
                      <th className="text-left px-5 py-3">Temp password</th>
                      <th className="text-right px-5 py-3">Progress</th>
                      <th className="text-right px-5 py-3">Score</th>
                      <th className="px-5 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {participants.map((p) => (
                      <tr key={p.user_id} className="border-t border-stone-100 hover:bg-stone-50" data-testid={`batch-participant-${p.user_id}`}>
                        <td className="px-5 py-3">
                          <span className="inline-flex items-center gap-2">
                            <Link to={`/admin/participants/${p.user_id}`} className="font-medium text-stone-900 hover:text-[#b81f24]">{p.name}</Link>
                            {p.must_change_password && (
                              <span data-testid={`pending-badge-${p.user_id}`} className="text-[9px] tracking-[0.12em] uppercase px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">Pending</span>
                            )}
                          </span>
                          <div className="text-xs text-stone-500">{p.employee_id || "—"} · {p.department || "—"}</div>
                        </td>
                        <td className="px-5 py-3 text-stone-600">{p.email}</td>
                        <td className="px-5 py-3">
                          <span className={`text-[10px] tracking-[0.18em] uppercase px-2 py-1 rounded-full border ${STATUS_BADGE[p.status]}`}>
                            {p.status.replace("_", " ")}
                          </span>
                        </td>
                        <td className="px-5 py-3">
                          {p.temp_password ? (
                            <code data-testid={`temp-password-${p.user_id}`} className="text-xs bg-stone-100 text-stone-800 px-1.5 py-0.5 rounded">{p.temp_password}</code>
                          ) : (
                            <span className="text-xs text-stone-400">set by user</span>
                          )}
                        </td>
                        <td className="px-5 py-3 text-right text-stone-700">
                          {p.scenarios_completed} / {p.scenarios_total}
                        </td>
                        <td className="px-5 py-3 text-right font-display text-base text-stone-900">{p.average_score ?? "—"}</td>
                        <td className="px-5 py-3 text-right">
                          <div className="inline-flex items-center gap-1">
                            <button onClick={() => resetPassword(p.user_id, p.name)} className="text-stone-400 hover:text-[#b81f24] p-1" title="Reset password" data-testid={`reset-password-${p.user_id}-btn`}>
                              <KeyRound className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                            <button onClick={() => removeParticipant(p.user_id)} className="text-stone-400 hover:text-red-600 p-1" title="Remove" data-testid={`remove-${p.user_id}-btn`}>
                              <UserMinus className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* HEATMAP */}
          <TabsContent value="heatmap" className="mt-6">
            {competency_heatmap.length === 0 ? (
              <div className="bg-white border border-dashed border-stone-200 rounded-3xl p-12 text-center text-stone-500">
                Once participants complete scenarios, the competency heatmap will appear here.
              </div>
            ) : (
              <div className="bg-white border border-stone-200 rounded-2xl p-6" data-testid="competency-heatmap">
                <div className="text-xs tracking-[0.18em] uppercase text-stone-500 mb-4 flex items-center gap-1.5">
                  <Activity className="w-3.5 h-3.5" strokeWidth={1.5} /> Average score by dimension · across batch
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[700px]">
                    <thead>
                      <tr className="text-[10px] tracking-[0.18em] uppercase text-stone-500">
                        <th className="text-left py-2 px-2">Scenario</th>
                        <th className="px-2 w-20">Empathy</th>
                        <th className="px-2 w-20">Clarity</th>
                        <th className="px-2 w-20">Assertive</th>
                        <th className="px-2 w-20">Listening</th>
                        <th className="px-2 w-20">Overall</th>
                        <th className="px-2 w-12">n</th>
                      </tr>
                    </thead>
                    <tbody>
                      {competency_heatmap.map((row) => (
                        <tr key={row.week} className="border-t border-stone-100">
                          <td className="py-3 px-2">
                            <div className="text-[10px] tracking-[0.18em] uppercase text-stone-400">Wk {row.week}</div>
                            <div className="font-medium text-stone-900 text-sm leading-tight">{row.theme}</div>
                            <div className="text-xs text-stone-500 truncate max-w-[280px]">{row.scenario_title}</div>
                          </td>
                          <td className="px-1.5"><HeatCell value={row.dimensions.empathy} /></td>
                          <td className="px-1.5"><HeatCell value={row.dimensions.clarity} /></td>
                          <td className="px-1.5"><HeatCell value={row.dimensions.assertiveness} /></td>
                          <td className="px-1.5"><HeatCell value={row.dimensions.listening} /></td>
                          <td className="px-1.5"><HeatCell value={row.score} /></td>
                          <td className="px-2 text-center text-xs text-stone-500">{row.participants_with_data}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-5 flex items-center gap-4 text-[10px] tracking-[0.18em] uppercase text-stone-500 flex-wrap">
                  <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{ backgroundColor: "#bbf7d0" }} />Strong (80+)</span>
                  <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{ backgroundColor: "#fef3c7" }} />Developing (65–79)</span>
                  <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{ backgroundColor: "#fed7aa" }} />Needs work (45–64)</span>
                  <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{ backgroundColor: "#fecaca" }} />Low (&lt;45)</span>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* ADD PARTICIPANTS DIALOG */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-w-2xl">
          <DialogHeader><DialogTitle className="font-display">Add participants to batch</DialogTitle></DialogHeader>
          <div className="mt-2">
            <div className="relative mb-3">
              <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.75} />
              <Input
                data-testid="batch-add-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email, employee ID, department…"
                className="pl-9 h-10"
              />
            </div>
            <div className="border border-stone-200 rounded-xl max-h-[400px] overflow-y-auto" data-testid="add-participants-list">
              {filteredPool.length === 0 ? (
                <div className="p-6 text-center text-stone-500 text-sm">No matching participants in the company pool.</div>
              ) : (
                filteredPool.map((p) => {
                  const checked = selected.has(p.id);
                  return (
                    <label key={p.id} className={`flex items-center gap-3 px-4 py-2.5 border-b last:border-b-0 border-stone-100 cursor-pointer ${checked ? "bg-red-50/40" : "hover:bg-stone-50"}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggle(p.id)} data-testid={`pool-${p.id}-check`} className="w-4 h-4 accent-[#b81f24]" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-stone-900 text-sm truncate">{p.name}</div>
                        <div className="text-xs text-stone-500 truncate">{p.email} · {p.employee_id || "—"} · {p.department || "—"}</div>
                      </div>
                    </label>
                  );
                })
              )}
            </div>
            <div className="mt-2 text-xs text-stone-500">
              {selected.size > 0 && <span className="inline-flex items-center gap-1.5 text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.75} /> {selected.size} selected</span>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="confirm-add-participants-btn" onClick={addSelected} disabled={adding || selected.size === 0} className="bg-[#b81f24] hover:bg-[#9a1a1f] text-white">
              {adding ? "Adding…" : `Add ${selected.size || ""} participant${selected.size === 1 ? "" : "s"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RESET ALL PASSWORDS SUMMARY */}
      <Dialog open={!!resetSummary} onOpenChange={(v) => !v && setResetSummary(null)}>
        <DialogContent className="rounded-2xl max-w-lg">
          <DialogHeader><DialogTitle className="font-display">New temporary passwords</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-stone-500 -mt-1">Share these with each participant. They&apos;ll be asked to change it on first login.</DialogDescription>
          <div className="mt-3 max-h-[50vh] overflow-y-auto border border-stone-200 rounded-xl" data-testid="reset-summary-list">
            <table className="w-full text-sm">
              <tbody>
                {(resetSummary || []).map((r) => (
                  <tr key={r.email} className="border-b border-stone-100 last:border-0">
                    <td className="px-3 py-2 text-stone-800">{r.name}</td>
                    <td className="px-3 py-2 text-stone-500">{r.email}</td>
                    <td className="px-3 py-2"><code className="text-xs bg-stone-100 px-1.5 py-0.5 rounded">{r.temp_password}</code></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setResetSummary(null)}>Close</Button>
            <Button
              data-testid="download-reset-credentials-btn"
              variant="outline"
              onClick={() => {
                const batchSlug = (data?.batch?.name || "batch").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
                downloadCredentialsXlsx(resetSummary, `${batchSlug}-reset-credentials.xlsx`);
              }}
              className="border-stone-300"
            >
              <Download className="w-4 h-4 mr-1.5" strokeWidth={1.75} /> Download Excel
            </Button>
            <Button data-testid="copy-reset-summary-btn" onClick={copyResetSummary} className="bg-[#b81f24] hover:bg-[#9a1a1f] text-white">Copy all</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* NEW PARTICIPANT (creates in company + links to this batch) */}
      <Dialog open={newPOpen} onOpenChange={setNewPOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-display">New participant</DialogTitle></DialogHeader>
          <form onSubmit={createParticipantInBatch} className="space-y-4 mt-2" data-testid="new-participant-in-batch-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-stone-600 mb-1.5 block">Employee ID *</label>
                <Input data-testid="np-employee-id-input" required value={npForm.employee_id} onChange={(e) => setNpForm({ ...npForm, employee_id: e.target.value })} className="h-10" />
              </div>
              <div>
                <label className="text-xs text-stone-600 mb-1.5 block">Full name *</label>
                <Input data-testid="np-name-input" required value={npForm.name} onChange={(e) => setNpForm({ ...npForm, name: e.target.value })} className="h-10" />
              </div>
            </div>
            <div>
              <label className="text-xs text-stone-600 mb-1.5 block">Email *</label>
              <Input data-testid="np-email-input" type="email" required value={npForm.email} onChange={(e) => setNpForm({ ...npForm, email: e.target.value })} className="h-10" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-stone-600 mb-1.5 block">Department</label>
                <Input data-testid="np-department-input" value={npForm.department} onChange={(e) => setNpForm({ ...npForm, department: e.target.value })} className="h-10" />
              </div>
              <div>
                <label className="text-xs text-stone-600 mb-1.5 block">Designation</label>
                <Input data-testid="np-designation-input" value={npForm.designation} onChange={(e) => setNpForm({ ...npForm, designation: e.target.value })} className="h-10" />
              </div>
            </div>
            <p className="text-[11px] text-stone-400">This creates the participant under the company and links them to this batch automatically.</p>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setNewPOpen(false)}>Cancel</Button>
              <Button data-testid="save-np-btn" type="submit" disabled={savingNp} className="bg-[#b81f24] hover:bg-[#9a1a1f] text-white">{savingNp ? "Saving…" : "Create & add"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* EDIT SESSION LIMIT DIALOG */}
      <Dialog open={limitOpen} onOpenChange={setLimitOpen}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader><DialogTitle className="font-display">Batch settings</DialogTitle></DialogHeader>
          <div className="mt-2 space-y-4">
            <div>
              <label className="text-xs text-stone-600 mb-1.5 block">Facilitator</label>
              <FacilitatorPicker
                testid="batch-facilitator-select"
                facilitators={facilitators}
                name={facName}
                userId={facUserId}
                onChange={({ facilitator_name, facilitator_user_id }) => { setFacName(facilitator_name); setFacUserId(facilitator_user_id); }}
              />
            </div>
            <div>
              <label className="text-xs text-stone-600 mb-1.5 block">Max roleplays per participant</label>
              <Input
                data-testid="session-limit-input"
                type="number"
                min="1"
                value={limitValue}
                onChange={(e) => setLimitValue(e.target.value)}
                placeholder="Leave empty for unlimited"
                className="h-10"
              />
              <p className="text-[11px] text-stone-400 mt-1.5">Empty means unlimited practice sessions.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setLimitOpen(false)}>Cancel</Button>
            <Button data-testid="save-session-limit-btn" onClick={saveLimit} disabled={savingLimit} className="bg-[#b81f24] hover:bg-[#9a1a1f] text-white">
              {savingLimit ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
